import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearClarity, initializeClarity } from '../utils/clarity';
import { loadGoogleAnalytics, clearGoogleAnalytics } from '../utils/ga';
import { useConsentManager, defaultConsent, acceptedConsent } from '../hooks/useConsentManager';

export const COOKIE_CONSENT_KEY = 'cookie_consent';

type CookieConsentProps = {
  showBanner?: boolean; // Forced override (used by CookiePreferencesButton)
  onPreferencesSaved?: () => void;
};

const CookieConsent = ({
  showBanner: forcedShowBanner,
  onPreferencesSaved,
}: CookieConsentProps) => {
  const navigate = useNavigate();

  const {
    consent,
    showBanner: hookShowBanner,
    setShowBanner,
    saveConsent,
    isInitialized,
    countryCode,
    consentMode,
  } = useConsentManager({ cookieKey: COOKIE_CONSENT_KEY });

  // Determine effective visibility
  const isBannerVisible = forcedShowBanner !== undefined ? forcedShowBanner : hookShowBanner;

  // Sync analytics with consent state
  useEffect(() => {
    if (!isInitialized) return;

    if (consent.analytics) {
      loadGoogleAnalytics();
      initializeClarity();
    } else {
      const googleWasLoaded = clearGoogleAnalytics();
      const clarityWasLoaded = clearClarity();
      // Unload already-running trackers rather than leaving cookieless collection active.
      // The preference is saved first; the next page load does not load these scripts.
      if (googleWasLoaded || clarityWasLoaded) window.location.reload();
    }
  }, [consent, isInitialized]);

  // Dismiss banner when preferences are saved from the Account → Privacy page
  useEffect(() => {
    const handleConsentSaved = () => setShowBanner(false);
    window.addEventListener('consentSaved', handleConsentSaved);
    return () => window.removeEventListener('consentSaved', handleConsentSaved);
  }, [setShowBanner]);

  const handleAcceptAll = () => {
    saveConsent(acceptedConsent);
    if (onPreferencesSaved) onPreferencesSaved();
  };

  const handleCustomise = () => {
    // Opening settings does not record a consent choice.
    navigate('/account#privacy');
  };

  if (!isBannerVisible && !forcedShowBanner) {
    return null;
  }

  const slideStyle = `
    @keyframes cookie-slide-up {
      from { transform: translateY(100%); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    .animate-cookie-slide-up {
      animation: cookie-slide-up 0.3s ease-out;
    }
  `;

  // ─── CCPA opt-out notice ───────────────────────────────────────────────────
  if (consentMode === 'ccpa' && !forcedShowBanner) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-9999 bg-white dark:bg-zinc-900 border-t border-gray-200 dark:border-zinc-700 shadow-2xl p-4 animate-cookie-slide-up">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col items-center gap-3 text-center">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                We use cookies and similar technologies to analyze traffic and improve your experience.{' '}
                <a
                  href="https://simplysend.email/legal/cookie-policy"
                  className="text-cyan-600 dark:text-cyan-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie Policy
                </a>
              </p>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                Manage your preferences anytime from <span className="font-medium text-gray-500 dark:text-gray-400">Account → Privacy</span>.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCustomise}
                className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors whitespace-nowrap"
              >
                Customize Settings
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="px-8 py-1.5 text-xs font-semibold rounded-full bg-cyan-600 hover:bg-cyan-700 text-zinc-100 transition-colors whitespace-nowrap"
              >
                OK
              </button>
            </div>
          </div>
        </div>
        <style>{slideStyle}</style>
      </div>
    );
  }

  // ─── GDPR opt-in banner ────────────────────────────────────────────────────
  return (
    <div className="fixed bottom-0 left-0 right-0 z-9999 bg-white dark:bg-zinc-900 border-t border-gray-200 dark:border-zinc-700 shadow-2xl p-4 animate-cookie-slide-up">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-center gap-4 text-center">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-zinc-100 mb-1 text-sm">
              We value your privacy
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              We use cookies to remember your settings and analyze usage.
              {countryCode && (
                <span className="ml-1 opacity-60">(Region: {countryCode})</span>
              )}
              {' '}
              <a
                href="https://simplysend.email/legal/cookie-policy"
                className="text-cyan-600 dark:text-cyan-400 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Cookie Policy
              </a>
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
              Manage your preferences anytime from <span className="font-medium text-gray-500 dark:text-gray-400">Account → Privacy</span>.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            <button type="button" onClick={() => { saveConsent(defaultConsent); onPreferencesSaved?.(); }} className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800"><span>Reject optional cookies</span></button>
            <button
              type="button"
              onClick={handleCustomise}
              className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors whitespace-nowrap"
            >
              Customise Settings
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="px-3 py-1.5 text-xs font-semibold rounded-full bg-cyan-600 hover:bg-cyan-700 text-zinc-100 transition-colors whitespace-nowrap"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
      <style>{slideStyle}</style>
    </div>
  );
};

export { CookieConsent };
export default CookieConsent;
